import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import ScrollToTop from './components/ScrollToTop'
import Header from './components/layout/Header'
import Footer from './components/layout/Footer'
import { usePageTracking } from './hooks/usePageTracking'

// Eager load Home for fast initial render
import Home from './pages/Home'

// Lazy load all other pages
const Pricing = lazy(() => import('./pages/Pricing'))
const Contact = lazy(() => import('./pages/Contact'))
const About = lazy(() => import('./pages/About'))
const Careers = lazy(() => import('./pages/Careers'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const Partners = lazy(() => import('./pages/Partners'))

// Feedback Loops
const FeedbackLoopsIndex = lazy(() => import('./pages/FeedbackLoops'))
const EventLoop = lazy(() => import('./pages/FeedbackLoops/EventLoop'))
const ValidationLoop = lazy(() => import('./pages/FeedbackLoops/ValidationLoop'))
const ProductLoop = lazy(() => import('./pages/FeedbackLoops/ProductLoop'))
const CustomerLoop = lazy(() => import('./pages/FeedbackLoops/CustomerLoop'))
const ServiceLoop = lazy(() => import('./pages/FeedbackLoops/ServiceLoop'))
const EmployeeLoop = lazy(() => import('./pages/FeedbackLoops/EmployeeLoop'))
const ChurnResearchIndex = lazy(() => import('./pages/FeedbackLoops/Customer/Churn'))
const ChurnAnalysis = lazy(() => import('./pages/FeedbackLoops/Customer/Churn/Analysis'))
const ChurnPrevention = lazy(() => import('./pages/FeedbackLoops/Customer/Churn/Prevention'))
const ChurnReduction = lazy(() => import('./pages/FeedbackLoops/Customer/Churn/Reduction'))

// Industries
const IndustriesIndex = lazy(() => import('./pages/Industries'))
const FinancialServicesIndex = lazy(() => import('./pages/Industries/FinancialServices'))
const BanksCreditUnions = lazy(() => import('./pages/Industries/FinancialServices/BanksCreditUnions'))
const RetirementPlanning = lazy(() => import('./pages/Industries/FinancialServices/RetirementPlanning'))
const Insurance = lazy(() => import('./pages/Industries/FinancialServices/Insurance'))
const HealthcareIndex = lazy(() => import('./pages/Industries/Healthcare'))
const Hospitals = lazy(() => import('./pages/Industries/Healthcare/Hospitals'))
const Clinics = lazy(() => import('./pages/Industries/Healthcare/Clinics'))
const Dental = lazy(() => import('./pages/Industries/Healthcare/Dental'))
const NonprofitIndex = lazy(() => import('./pages/Industries/Nonprofit'))
const Foundations = lazy(() => import('./pages/Industries/Nonprofit/Foundations'))
const Membership = lazy(() => import('./pages/Industries/Nonprofit/Membership'))
const SocialServices = lazy(() => import('./pages/Industries/Nonprofit/SocialServices'))
const TransportationIndex = lazy(() => import('./pages/Industries/Transportation'))
const Airlines = lazy(() => import('./pages/Industries/Transportation/Airlines'))
const Logistics = lazy(() => import('./pages/Industries/Transportation/Logistics'))
const PublicTransit = lazy(() => import('./pages/Industries/Transportation/PublicTransit'))
const TechnologyIndex = lazy(() => import('./pages/Industries/Technology'))
const SaaS = lazy(() => import('./pages/Industries/Technology/SaaS'))
const B2BSaaS = lazy(() => import('./pages/Industries/Technology/B2BSaaS'))
const ConsumerElectronics = lazy(() => import('./pages/Industries/Technology/ConsumerElectronics'))
const HospitalityIndex = lazy(() => import('./pages/Industries/Hospitality'))
const Hotels = lazy(() => import('./pages/Industries/Hospitality/Hotels'))
const Restaurants = lazy(() => import('./pages/Industries/Hospitality/Restaurants'))
const EducationIndex = lazy(() => import('./pages/Industries/Education'))
const HigherEd = lazy(() => import('./pages/Industries/Education/HigherEd'))
const K12 = lazy(() => import('./pages/Industries/Education/K12'))
const Research = lazy(() => import('./pages/Industries/Education/Research'))
const GovernmentIndex = lazy(() => import('./pages/Industries/Government'))
const Airports = lazy(() => import('./pages/Industries/Government/Airports'))
const Transit = lazy(() => import('./pages/Industries/Government/Transit'))
const Utilities = lazy(() => import('./pages/Industries/Government/Utilities'))
const Parks = lazy(() => import('./pages/Industries/Government/Parks'))
const ProfessionalServicesIndex = lazy(() => import('./pages/Industries/ProfessionalServices'))
const Agencies = lazy(() => import('./pages/Industries/ProfessionalServices/Agencies'))
const Staffing = lazy(() => import('./pages/Industries/ProfessionalServices/Staffing'))
const Consulting = lazy(() => import('./pages/Industries/ProfessionalServices/Consulting'))

// Blog
const BlogIndex = lazy(() => import('./pages/Blog'))
const BlogPost = lazy(() => import('./pages/Blog/BlogPost'))

// Loading fallback
function PageLoader() {
  return (
    <div className="min-h-[50vh] flex items-center justify-center">
      <div className="animate-pulse text-intelligence-400">Loading...</div>
    </div>
  )
}

function App() {
  // Track page views on route changes
  usePageTracking()

  return (
    <div className="min-h-screen flex flex-col">
      <ScrollToTop />
      <Header />
      <main className="flex-grow">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/about" element={<About />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/partners" element={<Partners />} />
            {/* Feedback Loops */}
            <Route path="/feedback-loops" element={<FeedbackLoopsIndex />} />
            <Route path="/feedback-loops/event" element={<EventLoop />} />
            <Route path="/feedback-loops/validation" element={<ValidationLoop />} />
            <Route path="/feedback-loops/product" element={<ProductLoop />} />
            <Route path="/feedback-loops/customer" element={<CustomerLoop />} />
            <Route path="/feedback-loops/service" element={<ServiceLoop />} />
            <Route path="/feedback-loops/employee" element={<EmployeeLoop />} />
            <Route path="/feedback-loops/customer/churn" element={<ChurnResearchIndex />} />
            <Route path="/feedback-loops/customer/churn/analysis" element={<ChurnAnalysis />} />
            <Route path="/feedback-loops/customer/churn/prevention" element={<ChurnPrevention />} />
            <Route path="/feedback-loops/customer/churn/reduction" element={<ChurnReduction />} />

            {/* Redirects from old /use-cases/* URLs */}
            <Route path="/use-cases" element={<Navigate to="/feedback-loops" replace />} />
            <Route path="/use-cases/customer-feedback" element={<Navigate to="/feedback-loops/customer" replace />} />
            <Route path="/use-cases/employee-engagement" element={<Navigate to="/feedback-loops/employee" replace />} />
            <Route path="/use-cases/product-research" element={<Navigate to="/feedback-loops/product" replace />} />
            <Route path="/use-cases/market-research" element={<Navigate to="/feedback-loops/validation" replace />} />
            <Route path="/use-cases/nps-followup" element={<Navigate to="/feedback-loops/customer" replace />} />
            <Route path="/use-cases/market-entry-research" element={<Navigate to="/feedback-loops/validation" replace />} />
            <Route path="/use-cases/event-intelligence" element={<Navigate to="/feedback-loops/event" replace />} />
            <Route path="/use-cases/product-market-fit" element={<Navigate to="/feedback-loops/validation" replace />} />
            <Route path="/use-cases/churn-research" element={<Navigate to="/feedback-loops/customer/churn" replace />} />
            <Route path="/use-cases/churn-research/analysis" element={<Navigate to="/feedback-loops/customer/churn/analysis" replace />} />
            <Route path="/use-cases/churn-research/prevention" element={<Navigate to="/feedback-loops/customer/churn/prevention" replace />} />
            <Route path="/use-cases/churn-research/reduction" element={<Navigate to="/feedback-loops/customer/churn/reduction" replace />} />

            {/* Industries */}
            <Route path="/industries" element={<IndustriesIndex />} />
            <Route path="/industries/financial-services" element={<FinancialServicesIndex />} />
            <Route path="/industries/financial-services/banks-credit-unions" element={<BanksCreditUnions />} />
            <Route path="/industries/financial-services/retirement-planning" element={<RetirementPlanning />} />
            <Route path="/industries/financial-services/insurance" element={<Insurance />} />
            <Route path="/industries/healthcare" element={<HealthcareIndex />} />
            <Route path="/industries/healthcare/hospitals" element={<Hospitals />} />
            <Route path="/industries/healthcare/clinics" element={<Clinics />} />
            <Route path="/industries/healthcare/dental" element={<Dental />} />
            <Route path="/industries/nonprofit" element={<NonprofitIndex />} />
            <Route path="/industries/nonprofit/foundations" element={<Foundations />} />
            <Route path="/industries/nonprofit/membership" element={<Membership />} />
            <Route path="/industries/nonprofit/social-services" element={<SocialServices />} />
            <Route path="/industries/transportation" element={<TransportationIndex />} />
            <Route path="/industries/transportation/airlines" element={<Airlines />} />
            <Route path="/industries/transportation/logistics" element={<Logistics />} />
            <Route path="/industries/transportation/public-transit" element={<PublicTransit />} />
            <Route path="/industries/technology" element={<TechnologyIndex />} />
            <Route path="/industries/technology/saas" element={<SaaS />} />
            <Route path="/industries/technology/b2b-saas" element={<B2BSaaS />} />
            <Route path="/industries/technology/consumer-electronics" element={<ConsumerElectronics />} />
            <Route path="/industries/hospitality" element={<HospitalityIndex />} />
            <Route path="/industries/hospitality/hotels" element={<Hotels />} />
            <Route path="/industries/hospitality/restaurants" element={<Restaurants />} />
            <Route path="/industries/education" element={<EducationIndex />} />
            <Route path="/industries/education/higher-ed" element={<HigherEd />} />
            <Route path="/industries/education/k12" element={<K12 />} />
            <Route path="/industries/education/research" element={<Research />} />
            <Route path="/industries/government" element={<GovernmentIndex />} />
            <Route path="/industries/government/airports" element={<Airports />} />
            <Route path="/industries/government/transit" element={<Transit />} />
            <Route path="/industries/government/utilities" element={<Utilities />} />
            <Route path="/industries/government/parks" element={<Parks />} />
            <Route path="/industries/professional-services" element={<ProfessionalServicesIndex />} />
            <Route path="/industries/professional-services/agencies" element={<Agencies />} />
            <Route path="/industries/professional-services/staffing" element={<Staffing />} />
            <Route path="/industries/professional-services/consulting" element={<Consulting />} />

            {/* Blog */}
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  )
}

export default App
